/**
 * The one sign-in intent the marketing site sends: start a pool (ISSUE-1101).
 *
 * Every "Start a pool" button on sportsmet.com links to
 * `CREATE_POOL_ENTRY_HREF` on the app host. A signed-out visitor is sent to
 * `/sign-in?redirect=/pools?dialog=create-pool` by `requireSignedIn`, so the
 * redirect itself is the intent: there is no second param to keep in step.
 *
 * Read by the sign-in screen (the context line above the form) and by
 * `onboardingRedirect.ts` (a new account keeps the intent through onboarding).
 * Pools itself still decides whether Create pool can open
 * (`isCreatePoolAvailable`, ISSUE-782).
 */

export const CREATE_POOL_DIALOG = "create-pool";
export const CREATE_POOL_ENTRY_HREF = `/pools?dialog=${CREATE_POOL_DIALOG}`;

/**
 * Shown above the sign-in form for a visitor who came to start a pool.
 * Design prompt 10-sign-in.md: one line above the form says why the visitor
 * is here (Q227), and there is no separate sign-up step, because typing your
 * email the first time is the account.
 */
export const CREATE_POOL_SIGN_IN_CONTEXT =
  "Sign in to start your pool. New here? Signing in with your email creates your account.";

// Only the path and the search matter. The base never leaves this function.
const PARSE_BASE = "https://app.invalid";

/**
 * True when `href` is an app path that opens Create pool on Pools. Anything
 * else, including an absolute URL or a protocol-relative one, is false.
 */
export function isCreatePoolIntent(href: string | undefined): boolean {
  if (href === undefined || !href.startsWith("/") || href.startsWith("//")) {
    return false;
  }
  let url: URL;
  try {
    url = new URL(href, PARSE_BASE);
  } catch {
    return false;
  }
  if (url.origin !== PARSE_BASE) return false;
  return (
    url.pathname === "/pools" &&
    url.searchParams.get("dialog") === CREATE_POOL_DIALOG
  );
}
